import { InviteRequestSchema, type InviteResponse } from '@fundline/shared/schemas/members';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { InviteLinkBox } from './InviteLinkBox.tsx';

const ROLE_OPTIONS = [
  { value: 'rep', label: 'Rep: sees only their own merchants' },
  { value: 'admin', label: 'Admin: manages the whole workspace' },
] as const;

export interface InviteFormProps {
  /** Sends the invite; resolves with the worker's answer. */
  onInvite: (body: {
    email: string;
    fullName: string;
    role: 'admin' | 'rep';
  }) => Promise<InviteResponse>;
  busy: boolean;
  error: string | null;
  /** Shown when the person invited is the first admin of a new workspace. */
  fixedRole?: 'admin';
}

export function InviteForm({ onInvite, busy, error, fixedRole }: InviteFormProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'rep'>(fixedRole ?? 'rep');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    name: string;
    email: string;
    response: InviteResponse;
  } | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = InviteRequestSchema.safeParse({ email, fullName, role });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
      );
      return;
    }
    setFieldErrors({});
    setResult(null);
    try {
      const response = await onInvite(parsed.data);
      setResult({ name: parsed.data.fullName, email: parsed.data.email, response });
      setEmail('');
      setFullName('');
    } catch {
      // The error is shown through the `error` prop.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Full name"
          autoComplete="off"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={fieldErrors.fullName}
        />
        <TextField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        {fixedRole ? null : (
          <SelectField
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value === 'admin' ? 'admin' : 'rep')}
            options={ROLE_OPTIONS}
          />
        )}
        <div className="flex items-end sm:col-span-2">
          <Button type="submit" busy={busy}>
            Send invite
          </Button>
        </div>
      </form>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {result?.response.inviteLink ? (
        <InviteLinkBox name={result.name} link={result.response.inviteLink} />
      ) : null}
      {result && !result.response.inviteLink ? (
        <Alert tone="success">
          Invite emailed to {result.email}. The link works once and expires after 24 hours.
        </Alert>
      ) : null}
    </div>
  );
}
